export const APPROVED_SUBJECT_STATUSES = new Set([
  'APPROVED_21K',
  'APPROVED_PARTNER',
  'APPROVED_VIA_BYPASS',
  'SETTLED_APPROVED',
]);

export const REJECTED_SUBJECT_STATUSES = new Set(['REJECTED_21K', 'REJECTED_PARTNER']);

export interface SubjectOutcomeFields {
  itemStatus: string;
}

export interface SubjectDisplayContext {
  applicationStatus: string;
  bagStatus?: string | null;
}

export interface SubjectRejectionFields extends SubjectOutcomeFields {
  id: string;
  subjectName: string;
  syllabusCode?: string | null;
  rejectionReason?: string | null;
  rejectionComment?: string | null;
  rejectedAt?: string | null;
  reviewerParty?: string | null;
  stepName?: string | null;
}

export function isApprovedSubject(subject: SubjectOutcomeFields): boolean {
  return APPROVED_SUBJECT_STATUSES.has(subject.itemStatus);
}

export function isRejectedSubject(subject: SubjectOutcomeFields): boolean {
  return REJECTED_SUBJECT_STATUSES.has(subject.itemStatus);
}

export function subjectOutcomeLabel(
  subject: SubjectOutcomeFields,
  context?: SubjectDisplayContext,
): string {
  if (isApprovedSubject(subject)) return 'Approved';
  if (isRejectedSubject(subject)) return 'Not approved';

  if (subject.itemStatus === 'REQUESTED') {
    if (context?.applicationStatus === 'UNDER_REVIEW') return 'Pending review';
    if (
      context?.applicationStatus === 'REGISTERED'
      || context?.bagStatus === 'DRAFT'
      || context?.applicationStatus === 'CHANGES_REQUESTED'
    ) {
      return 'Selected';
    }
    return 'Pending review';
  }

  return subject.itemStatus.replaceAll('_', ' ').toLowerCase();
}

export function subjectOutcomeBadgeClass(subject: SubjectOutcomeFields): string {
  if (isApprovedSubject(subject)) return 'badge badge-success';
  if (isRejectedSubject(subject)) return 'badge badge-neutral';
  if (subject.itemStatus === 'REQUESTED') return 'badge badge-neutral';
  return 'badge badge-info';
}

export function reviewerPartyLabel(party: string | null | undefined): string | null {
  if (!party) return null;
  if (party === 'TWENTYONEK') return '21K School';
  if (party === 'PARTNER_SCHOOL') return 'Partner school';
  return party.replaceAll('_', ' ').toLowerCase();
}

export function formatRejectionWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Asia/Kolkata',
  }).format(date);
}

export function hasRejectionDetails(subject: SubjectRejectionFields): boolean {
  return Boolean(
    displayRejectionReason(subject)
    || displayRejectionComment(subject)
    || subject.rejectedAt
    || subject.reviewerParty
    || subject.stepName,
  );
}

/** Generic code label — the parent's real explanation is usually in the comment. */
export function isGenericOtherReason(reason: string | null | undefined): boolean {
  if (!reason) return false;
  const normalized = reason.trim().toLowerCase();
  return normalized === 'other reason' || normalized === 'other';
}

/** Prefer the reviewer's free-text note over the canned "Other reason" label. */
export function displayRejectionReason(subject: SubjectRejectionFields): string | null {
  const reason = subject.rejectionReason?.trim() || null;
  const note = subject.rejectionComment?.trim() || null;
  if (note && isGenericOtherReason(reason)) return note;
  return reason;
}

export function displayRejectionComment(subject: SubjectRejectionFields): string | null {
  const reason = subject.rejectionReason?.trim() || null;
  const note = subject.rejectionComment?.trim() || null;
  if (!note) return null;
  if (isGenericOtherReason(reason) && note === displayRejectionReason(subject)) return null;
  return note;
}
